/* =====================================================================
 *  HEADTEACHER PORTAL + panels (Teachers, Approvals, Messages,
 *  Appointments, Publish report cards)
 * ===================================================================== */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { schoolSystemApi, GRADES, downloadPdf } from "@/lib/schoolSystemApi";
import {
  Buildings, ArrowLeft, CheckCircle, XCircle,
  Broadcast, WhatsappLogo, PaperPlaneTilt, ChartBar, Printer,
} from "@phosphor-icons/react";
import { F, SelectField } from "./shared";
import { SchoolBrandingHeader } from "./BrandingAndPosition";

/**
 * Renders an approval item as a human-readable form-style preview instead of
 * the raw JSON dump the HT used to see. Each `kind` mirrors the fields the
 * teacher submitted on the source form so the HT can review at a glance.
 */
function ApprovalPreview({ kind, target }) {
  const t = target || {};
  const Row = ({ label, value }) => (value === undefined || value === null || value === "") ? null : (
    <div className="grid grid-cols-3 gap-2 text-sm py-1 border-b border-white/5 last:border-b-0">
      <p className="text-white/50 text-xs uppercase tracking-wider">{label}</p>
      <p className="col-span-2 text-white/90 whitespace-pre-wrap break-words">{String(value)}</p>
    </div>
  );
  const box = "bg-white/[0.03] border border-white/10 rounded-md p-3";

  if (kind === "record") {
    return (
      <div className={box} data-testid="preview-record">
        <Row label="Student"     value={t.student_name || t.student_id} />
        <Row label="Subject"     value={t.subject_name || t.subject_id} />
        <Row label="Term"        value={t.term} />
        <Row label="Year"        value={t.academic_year} />
        <Row label="CA score"    value={t.ca_score} />
        <Row label="Exam score"  value={t.exam_score} />
        <Row label="Total"       value={t.total} />
        <Row label="Grade"       value={t.grade_letter} />
        <Row label="Remark"      value={t.remark} />
      </div>
    );
  }
  if (kind === "homework") {
    return (
      <div className={box} data-testid="preview-homework">
        <Row label="Class"       value={t.grade} />
        <Row label="Subject"     value={t.subject_name || t.subject_id} />
        <Row label="Title"       value={t.title} />
        <Row label="Description" value={t.description} />
        <Row label="Due date"    value={t.due_date} />
      </div>
    );
  }
  if (kind === "lesson_note") {
    return (
      <div className={box} data-testid="preview-lesson-note">
        <Row label="Class"       value={t.grade} />
        <Row label="Subject"     value={t.subject_name || t.subject_id} />
        <Row label="Week"        value={t.week} />
        <Row label="Title"       value={t.title} />
        <Row label="Notes"       value={t.body} />
        {Array.isArray(t.attachments) && t.attachments.length > 0 && (
          <div className="pt-2">
            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Attachments</p>
            <div className="space-y-1">
              {t.attachments.map((f, i) => (
                <a
                  key={i}
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sky-300 hover:text-sky-200 text-sm truncate"
                >
                  📎 {f.name || f.url} {f.size_kb ? `(${f.size_kb} KB)` : ""}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  if (kind === "behaviour") {
    return (
      <div className={box} data-testid="preview-behaviour">
        <Row label="Student"    value={t.student_name || t.student_id} />
        <Row label="Term"       value={t.term} />
        <Row label="Year"       value={t.academic_year} />
        <Row label="Conduct"    value={t.conduct} />
        <Row label="Attitude"   value={t.attitude} />
        <Row label="Interest"   value={t.interest} />
        <Row label="Discipline" value={t.discipline} />
      </div>
    );
  }
  if (kind === "fee") {
    return (
      <div className={box} data-testid="preview-fee">
        <Row label="Student"          value={t.student_name || t.student_id} />
        <Row label="Fee type"         value={t.fee_type} />
        <Row label="Description"      value={t.description} />
        <Row label="Amount due"       value={t.amount_due} />
        <Row label="Amount paid"      value={t.amount_paid} />
        <Row label="Due date"         value={t.due_date} />
        <Row label="Instructions"     value={t.payment_instructions} />
        <Row label="Term / Year"      value={[t.term, t.academic_year].filter(Boolean).join(" · ")} />
      </div>
    );
  }
  if (kind === "attendance") {
    return (
      <div className={box} data-testid="preview-attendance">
        <Row label="Student" value={t.student_name || t.student_id} />
        <Row label="Day"     value={t.day} />
        <Row label="Status"  value={t.status} />
      </div>
    );
  }
  if (kind === "homework_marking_scheme") {
    const c = t.content || {};
    const items = Array.isArray(c.items) ? c.items : [];
    return (
      <div className={box} data-testid="preview-homework-scheme">
        <Row label="Homework"    value={t.hw_label || t.homework_id} />
        <Row label="Class"       value={t.grade} />
        <Row label="Total marks" value={t.total_marks || c.total_marks || "—"} />
        {c.title && <Row label="Title" value={c.title} />}
        <div className="pt-3">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-2">
            Questions &amp; expected answers ({items.length})
          </p>
          <ol className="space-y-2">
            {items.map((it, i) => (
              <li key={i} className="p-3 rounded bg-black/30 border border-white/10">
                <p className="text-sm text-white/95 font-medium">
                  <span className="text-amber-300">Q{i + 1}.</span> {it.q || "—"}
                  {typeof it.marks !== "undefined" && (
                    <span className="ml-2 text-xs text-amber-300">
                      ({it.marks} mark{Number(it.marks) === 1 ? "" : "s"})
                    </span>
                  )}
                </p>
                {it.expected_answer && (
                  <p className="text-xs text-emerald-300 mt-2 whitespace-pre-wrap break-words">
                    <span className="font-bold">Expected: </span>{it.expected_answer}
                  </p>
                )}
                {Array.isArray(it.acceptable_variants) && it.acceptable_variants.length > 0 && (
                  <p className="text-xs text-emerald-200/80 mt-1 whitespace-pre-wrap break-words">
                    <span className="font-bold">Also acceptable: </span>{it.acceptable_variants.join(" · ")}
                  </p>
                )}
                {it.marking_criteria && (
                  <p className="text-xs text-white/70 mt-1 whitespace-pre-wrap break-words">
                    <span className="font-bold">Criteria: </span>{it.marking_criteria}
                  </p>
                )}
              </li>
            ))}
          </ol>
          {c.general_guidance && (
            <div className="mt-3 p-3 rounded bg-amber-500/10 border border-amber-500/20 text-xs text-amber-100 whitespace-pre-wrap break-words">
              <span className="font-bold">General guidance: </span>{c.general_guidance}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (kind === "homework_score") {
    const pct = typeof t.percentage === "number" ? `${t.percentage}%` : "—";
    return (
      <div className={box} data-testid="preview-homework-score">
        <Row label="Homework"   value={t.hw_label || t.homework_id} />
        <Row label="Class"      value={t.grade} />
        <Row label="Student"    value={t.student_name || t.student_id} />
        <Row label="Score"      value={`${t.score} / ${t.max_score} (${pct})`} />
        {t.remarks && <Row label="Remarks" value={t.remarks} />}
      </div>
    );
  }
  // Fallback for any future kind: render every field as a labelled row.
  return (
    <div className={box} data-testid="preview-generic">
      {Object.entries(t).map(([k, v]) => (
        <Row
          key={k}
          label={k.replace(/_/g, " ")}
          value={typeof v === "object" ? JSON.stringify(v) : v}
        />
      ))}
    </div>
  );
}

export default function HeadteacherPortal() {
  const { schoolId } = useParams();
  const [tab, setTab] = useState("assignments");
  return (
    <div className="school-portal max-w-6xl mx-auto p-4 md:p-8 space-y-8" data-testid="ht-portal">
      <Link to="/school-system" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
        <ArrowLeft size={14} weight="bold" /> School System
      </Link>
      <SchoolBrandingHeader schoolId={schoolId} portal="headteacher" />
      <h1 className="font-heading text-2xl font-black flex items-center gap-2">
        <Buildings size={26} weight="fill" className="text-emerald-400" /> Headteacher Portal
      </h1>
      <AttendanceDashboardPanel schoolId={schoolId} />
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[#121212] border border-white/10 flex-wrap gap-1 p-1 h-auto w-full justify-start">
          <TabsTrigger value="assignments" data-testid="ht-tab-assignments">Teachers</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="ht-tab-approvals">Approvals</TabsTrigger>
          <TabsTrigger value="messages" data-testid="ht-tab-messages">Parent messages</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="ht-tab-appointments">Appointments</TabsTrigger>
          <TabsTrigger value="publish" data-testid="ht-tab-publish">Publish reports</TabsTrigger>
          <TabsTrigger value="print" data-testid="ht-tab-print">Print / PDF</TabsTrigger>
          <TabsTrigger value="promote" data-testid="ht-tab-promote">Promote</TabsTrigger>
          <TabsTrigger value="grading" data-testid="ht-tab-grading">Grading policy</TabsTrigger>
          <TabsTrigger value="register" data-testid="ht-tab-register">Admission register</TabsTrigger>
          <TabsTrigger value="fees" data-testid="ht-tab-fees">Master fees</TabsTrigger>
          <TabsTrigger value="fee-entries" data-testid="ht-tab-fee-entries">Fee entries</TabsTrigger>
          <TabsTrigger value="approved" data-testid="ht-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="ht-tab-broadcast">Broadcast</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="ht-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="publish-perf" data-testid="ht-tab-publish-perf">Publish performance</TabsTrigger>
          <TabsTrigger value="branding" data-testid="ht-tab-branding">Branding</TabsTrigger>
        </TabsList>
        <TabsContent value="assignments" className="mt-8"><AssignmentsPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="approvals" className="mt-8"><ApprovalsPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="messages" className="mt-8"><MessagesPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="appointments" className="mt-8"><AppointmentsPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="publish" className="mt-8"><PublishReportsPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="print" className="mt-8"><PrintReportPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="promote" className="mt-8"><PromotePanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="grading" className="mt-8"><GradingPolicyPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="register" className="mt-8"><AdmissionRegisterPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="fees" className="mt-8"><MasterFeesPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="fee-entries" className="mt-8"><FeeEntriesPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="approved" className="mt-8"><ApprovedHistoryPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="broadcast" className="mt-8"><ParentBroadcastPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="calendar" className="mt-8"><CalendarPanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="publish-perf" className="mt-8"><PublishPerformancePanel schoolId={schoolId}/></TabsContent>
        <TabsContent value="branding" className="mt-8"><BrandingPanel schoolId={schoolId}/></TabsContent>
      </Tabs>
    </div>
  );
}

function AttendanceDashboardPanel({ schoolId }) {
  const [today, setToday] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [view, setView] = useState("today"); // "today" | "term"
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfGrade, setPdfGrade] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await schoolSystemApi.attendanceDashboard(schoolId, today);
      setData(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load attendance");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [schoolId, today]);

  const approveAll = async () => {
    if (!window.confirm(`Approve every pending attendance record for ${today}?`)) return;
    setApproving(true);
    try {
      const r = await schoolSystemApi.approveAllAttendanceToday(schoolId, today);
      toast.success(`Approved ${r.approved} attendance record(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setApproving(false); }
  };

  const rows = view === "today"
    ? (data?.today?.by_grade || [])
    : (data?.term_to_date?.by_grade || []);
  const school = view === "today" ? data?.today?.school : data?.term_to_date?.school;

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="attendance-dashboard">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <ChartBar size={20} weight="fill" className="text-sky-400" />
          School Attendance Dashboard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <F label="Day">
            <Input
              type="date"
              value={today}
              onChange={(e) => setToday(e.target.value)}
              data-testid="att-dash-day"
              className="bg-white/5 border-white/10 min-h-[44px]"
            />
          </F>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              data-testid="att-dash-view-today"
              onClick={() => setView("today")}
              className={`min-h-[44px] ${view === "today"
                ? "bg-sky-500 hover:bg-sky-600 text-black font-bold"
                : "bg-white/5 hover:bg-white/10 text-white border border-white/10"}`}
            >
              Today's Snapshot
            </Button>
            <Button
              size="sm"
              data-testid="att-dash-view-term"
              onClick={() => setView("term")}
              className={`min-h-[44px] ${view === "term"
                ? "bg-sky-500 hover:bg-sky-600 text-black font-bold"
                : "bg-white/5 hover:bg-white/10 text-white border border-white/10"}`}
            >
              Term-to-Date Totals
            </Button>
          </div>
        </div>

        {busy && !data && <p className="text-white/50 text-sm">Loading…</p>}

        {data && (
          <div className="overflow-x-auto rounded-md border border-white/10">
            <table className="w-full text-sm" data-testid={`att-dash-table-${view}`}>
              <thead className="bg-white/[0.04] text-white/60 uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="text-left p-3">Class</th>
                  <th className="text-right p-3">Present</th>
                  <th className="text-right p-3">Absent</th>
                  <th className="text-right p-3">Late</th>
                  {view === "today" && <th className="text-right p-3">Unmarked</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={view === "today" ? 5 : 4} className="p-4 text-center text-white/50">No students yet.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.grade} className="border-t border-white/5" data-testid={`att-dash-row-${r.grade}`}>
                    <td className="p-3 font-medium">{r.grade}</td>
                    <td className="p-3 text-right text-emerald-300">{r.present}</td>
                    <td className="p-3 text-right text-red-300">{r.absent}</td>
                    <td className="p-3 text-right text-amber-300">{r.late}</td>
                    {view === "today" && <td className="p-3 text-right text-white/60">{r.unmarked}</td>}
                  </tr>
                ))}
                {school && (
                  <tr className="border-t border-white/20 bg-white/[0.03] font-bold" data-testid="att-dash-row-school">
                    <td className="p-3">School-wide</td>
                    <td className="p-3 text-right text-emerald-300">{school.present}</td>
                    <td className="p-3 text-right text-red-300">{school.absent}</td>
                    <td className="p-3 text-right text-amber-300">{school.late}</td>
                    {view === "today" && <td className="p-3 text-right text-white/60">{school.unmarked}</td>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="pt-3 border-t border-white/10">
          <Button
            data-testid="att-dash-approve-all-btn"
            onClick={approveAll}
            disabled={approving || !(data?.pending_today > 0)}
            className="w-full sm:w-auto min-h-[44px] bg-emerald-500 hover:bg-emerald-600 text-black font-bold gap-2"
          >
            <CheckCircle size={16} weight="fill" />
            {approving
              ? "Approving…"
              : `Approve All Today's Attendance${data?.pending_today ? ` (${data.pending_today})` : ""}`}
          </Button>
          {data && !data.pending_today && (
            <p className="text-[11px] text-white/40 mt-2">No pending attendance for {today}.</p>
          )}
        </div>

        <div className="pt-4 border-t border-white/10 space-y-2" data-testid="att-dash-pdf-block">
          <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
            Attendance register (PDF) for {today}
          </p>
          <p className="text-xs text-white/50 max-w-xl">
            Download a printable Unicode-safe roll call for a specific class,
            or leave the class blank for a whole-school register. Includes
            every active student with their status and totals.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">
                Class (blank = all)
              </label>
              <select value={pdfGrade}
                      data-testid="att-dash-pdf-grade"
                      onChange={(e) => setPdfGrade(e.target.value)}
                      className="h-10 min-w-[10rem] px-3 rounded-md bg-white/5 border border-white/10 text-sm">
                <option value="">All grades</option>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <Button data-testid="att-dash-pdf-download"
                    disabled={pdfBusy}
                    onClick={async () => {
                      setPdfBusy(true);
                      try {
                        await downloadPdf(
                          schoolSystemApi.attendancePdfPath(schoolId, today, pdfGrade || null),
                          `attendance-${pdfGrade || "all-grades"}-${today}.pdf`,
                        );
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e.message || "PDF failed");
                      } finally { setPdfBusy(false); }
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold gap-1">
              <Printer size={16} weight="bold" />
              {pdfBusy ? "Preparing…" : "Download PDF"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AssignmentsPanel({ schoolId }) {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [grade, setGrade] = useState("Grade 1");
  const load = async () => {
    try { const r = await schoolSystemApi.listAssignments(schoolId); setItems(r.items||[]); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [schoolId]);
  const add = async () => {
    if (!email.includes("@")) return toast.error("Valid email required");
    try {
      await schoolSystemApi.assignTeacher({ school_id: schoolId, teacher_email: email, teacher_name: name, grade });
      toast.success("Teacher assigned"); setEmail(""); setName(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <F label="Teacher email"><Input data-testid="assign-email" value={email} onChange={(e)=>setEmail(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <F label="Teacher name"><Input value={name} onChange={(e)=>setName(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <SelectField label="Grade" testId="assign-grade" value={grade} onChange={setGrade} options={GRADES.map(g=>({value:g,label:g}))}/>
        <div className="flex items-end">
          <Button data-testid="assign-btn" onClick={add} className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold w-full">Assign</Button>
        </div>
      </div>
      <div className="pt-4 border-t border-white/10 space-y-2">
        <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Assigned teachers</p>
        {items.length === 0 && <p className="text-white/50 text-sm">No teachers assigned yet.</p>}
        {items.map((a) => (
          <div key={a.assignment_id} className="flex items-center justify-between p-3 rounded bg-white/[0.03]" data-testid={`assignment-${a.assignment_id}`}>
            <p className="text-sm"><b>{a.teacher_email}</b> · {a.grade} {a.revoked && <Badge className="ml-2 bg-red-500/20 text-red-300">Revoked</Badge>}</p>
            {!a.revoked ? (
              <Button
                size="sm"
                variant="outline"
                className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400"
                onClick={async ()=>{await schoolSystemApi.revokeAssignment(a.assignment_id); load();}}
              >
                Revoke
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                data-testid={`assignment-delete-${a.assignment_id}`}
                className="border-white/20 bg-transparent hover:bg-white/5 text-white/70"
                onClick={async () => {
                  if (!window.confirm(`Permanently remove ${a.teacher_email} · ${a.grade} from the list? This cannot be undone.`)) return;
                  try { await schoolSystemApi.hardDeleteAssignment(a.assignment_id); toast.success("Removed"); load(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }}
              >
                Delete
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalsPanel({ schoolId }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = async () => {
    try { const r = await schoolSystemApi.listApprovals(schoolId); setItems(r.items||[]); setSelected(new Set()); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [schoolId]);
  const decide = async (id, decision) => {
    try { await schoolSystemApi.decideApproval({ approval_id: id, decision }); toast.success(decision); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(a => a.approval_id)));
  };
  const bulkDecide = async (decision) => {
    if (selected.size === 0) return toast.error("Select at least one item");
    if (decision !== "approve" &&
        !window.confirm(`${decision === "reject" ? "Reject" : "Return"} ${selected.size} selected item(s)?`)) {
      return;
    }
    setBulkBusy(true);
    try {
      const r = await schoolSystemApi.decideApprovalBulk({
        approval_ids: Array.from(selected),
        decision,
      });
      const failMsg = (r.failures || []).length
        ? ` · ${r.failures.length} failed`
        : "";
      toast.success(`${r.applied}/${r.requested} ${decision}d${failMsg}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk decide failed");
    } finally { setBulkBusy(false); }
  };
  return (
    <div className="space-y-4">
      <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Pending approvals</p>
      {items.length === 0 && <p className="text-white/50 text-sm">No pending approvals.</p>}
      {items.length > 0 && (
        <div className="sticky top-0 z-10 bg-black/60 backdrop-blur border border-white/10
                        rounded-md p-2 flex flex-wrap items-center gap-2"
              data-testid="bulk-approve-bar">
          <label className="flex items-center gap-2 text-xs text-white/70 select-none cursor-pointer">
            <input
              type="checkbox"
              data-testid="bulk-toggle-all"
              checked={selected.size > 0 && selected.size === items.length}
              onChange={toggleAll}
            />
            <span>
              {selected.size === 0
                ? "Select all"
                : selected.size === items.length
                ? "Deselect all"
                : `${selected.size} selected`}
            </span>
          </label>
          <div className="flex-1" />
          <Button size="sm"
                  data-testid="bulk-approve-btn"
                  disabled={bulkBusy || selected.size === 0}
                  onClick={() => bulkDecide("approve")}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold gap-1">
            <CheckCircle size={12} weight="fill"/>
            Approve {selected.size || ""} selected
          </Button>
          <Button size="sm" variant="outline"
                  data-testid="bulk-return-btn"
                  disabled={bulkBusy || selected.size === 0}
                  onClick={() => bulkDecide("request_revision")}
                  className="border-white/20 bg-transparent hover:bg-white/5">
            Return
          </Button>
          <Button size="sm" variant="outline"
                  data-testid="bulk-reject-btn"
                  disabled={bulkBusy || selected.size === 0}
                  onClick={() => bulkDecide("reject")}
                  className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400 gap-1">
            <XCircle size={12} weight="bold"/> Reject
          </Button>
        </div>
      )}
      {items.map((a) => (
        <Card key={a.approval_id} className="bg-[#121212] border-white/10" data-testid={`approval-${a.approval_id}`}>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid={`bulk-select-${a.approval_id}`}
                  checked={selected.has(a.approval_id)}
                  onChange={() => toggle(a.approval_id)}
                />
                <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/40 capitalize">
                  {String(a.kind).replace(/_/g, " ")}
                </Badge>
              </div>
              <p className="text-[11px] text-white/40">by {a.submitted_by}</p>
            </div>
            <ApprovalPreview kind={a.kind} target={a.target || {}} />
            <div className="flex gap-2 flex-wrap pt-2 border-t border-white/5 action-bar">
              <Button data-testid={`approve-${a.approval_id}`} size="sm" onClick={()=>decide(a.approval_id,"approve")} className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold gap-1"><CheckCircle size={12} weight="fill"/> Approve</Button>
              <Button size="sm" variant="outline" onClick={()=>decide(a.approval_id,"reject")} className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400 gap-1"><XCircle size={12} weight="bold"/> Reject</Button>
              <Button size="sm" variant="outline" onClick={()=>decide(a.approval_id,"request_revision")} className="border-white/20 bg-transparent hover:bg-white/5">Request revision</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MessagesPanel({ schoolId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { schoolSystemApi.listParentMessages(schoolId).then(r => setItems(r.items||[])).catch(()=>{}); }, [schoolId]);
  return (
    <div className="space-y-4">
      <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Parent messages</p>
      {items.length === 0 && <p className="text-white/50 text-sm">No parent messages yet.</p>}
      {items.map((m) => (
        <Card key={m.message_id} className="bg-[#121212] border-white/10">
          <CardContent className="p-3">
            <p className="font-heading font-bold text-sm">{m.subject} <Badge className="ml-2 bg-white/10">{m.kind}</Badge></p>
            <p className="text-xs text-white/60">From student {m.student_id}</p>
            <p className="text-sm text-white/85 mt-1 whitespace-pre-wrap">{m.body}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AppointmentsPanel({ schoolId }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const r = await schoolSystemApi.listAppointments(schoolId); setItems(r.items||[]); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [schoolId]);
  const decide = async (id, decision) => {
    try { await schoolSystemApi.decideAppointment(id, { decision }); toast.success(decision); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Appointment requests</p>
      {items.length === 0 && <p className="text-white/50 text-sm">No appointment requests yet.</p>}
      {items.map((a) => (
        <Card key={a.appointment_id} className="bg-[#121212] border-white/10">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm"><b>{a.purpose}</b> — {a.preferred_date} {a.preferred_time || ""}</p>
              <p className="text-xs text-white/60">{a.initiator === "parent" ? "Parent request" : "You initiated"} · {a.meeting_type}</p>
              <Badge className={`mt-1 ${a.status === "accept" || a.status === "accepted" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10"}`}>{a.status}</Badge>
            </div>
            {a.status === "pending" && a.initiator === "parent" && (
              <div className="flex gap-1">
                <Button size="sm" onClick={()=>decide(a.appointment_id,"accept")} className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">Accept</Button>
                <Button size="sm" variant="outline" onClick={()=>decide(a.appointment_id,"decline")} className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400">Decline</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PublishReportsPanel({ schoolId }) {
  const [grade, setGrade] = useState("Grade 1");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState("Term 1");
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await schoolSystemApi.listPublishedReportCards(schoolId);
      setHistory(r.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-line */ }, [schoolId]);

  const publish = async () => {
    if (!grade || !year || !term) return toast.error("Grade, year and term are required");
    if (!dryRun && !window.confirm(
      `Publish report cards for every student in ${grade} — ${year} ${term}?\n\n` +
      "Each parent will receive an email with a PDF report card plus a WhatsApp deep link to reach the school."
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await schoolSystemApi.publishReportCards({
        school_id: schoolId, grade, academic_year: year, term, dry_run: dryRun,
      });
      setResult(r);
      toast.success(dryRun
        ? `Dry run: would send ${r.total_students - r.no_parent_email} emails`
        : `Published — sent ${r.sent}, failed ${r.failed}, no email ${r.no_parent_email}`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="publish-reports-panel">
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Broadcast size={20} weight="fill" className="text-fuchsia-400"/> Publish report cards
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-white/70">
            At the end of a term, publish every student's report card in one click. Each parent receives an email
            with a PDF report card attached plus a <span className="text-emerald-300 inline-flex items-center gap-1">
              <WhatsappLogo size={14} weight="fill"/> WhatsApp
            </span> deep link to reach the school instantly.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SelectField label="Grade" testId="pub-grade" value={grade} onChange={setGrade} options={GRADES.map(g=>({value:g,label:g}))}/>
            <F label="Academic year"><Input data-testid="pub-year" value={year} onChange={(e)=>setYear(e.target.value)} className="bg-white/5 border-white/10"/></F>
            <F label="Term"><Input data-testid="pub-term" value={term} onChange={(e)=>setTerm(e.target.value)} className="bg-white/5 border-white/10"/></F>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" data-testid="pub-dry" checked={dryRun} onChange={(e)=>setDryRun(e.target.checked)}/>
            Dry run (generate PDFs but don't send emails)
          </label>
          <div className="pt-2">
            <Button data-testid="pub-btn" onClick={publish} disabled={busy}
              className="bg-fuchsia-500 hover:bg-fuchsia-600 text-black font-bold gap-2">
              <PaperPlaneTilt size={16} weight="fill"/>
              {busy ? "Publishing…" : dryRun ? "Run dry publish" : "Publish & email parents"}
            </Button>
          </div>
          {result && (
            <Card className="bg-white/[0.03] border-white/10 mt-4">
              <CardContent className="p-4 text-sm space-y-1" data-testid="publish-result">
                <p><b>Total students:</b> {result.total_students}</p>
                <p><b>Sent:</b> <span className="text-emerald-300">{result.sent}</span></p>
                <p><b>Failed:</b> <span className="text-red-300">{result.failed}</span></p>
                <p><b>No parent email:</b> <span className="text-amber-300">{result.no_parent_email}</span></p>
                {result.email_disabled && (
                  <p className="text-[11px] text-yellow-300">
                    Email delivery is disabled (RESEND_API_KEY not set). PDFs generated but not delivered.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Recent publications</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {history.length === 0 && <p className="text-white/50 text-sm">Nothing published yet.</p>}
          {history.map((h, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded bg-white/[0.03] text-xs" data-testid={`pub-history-${i}`}>
              <div>
                <p className="text-white/80">{h.grade} · {h.academic_year} · {h.term}</p>
                <p className="text-white/50">Student {h.student_id} · Avg {h.average} · Pos {h.overall_position || "—"}</p>
              </div>
              <p className="text-white/40">{new Date(h.published_at).toLocaleString()}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PrintReportPanel({ schoolId }) {
  const [grade, setGrade] = useState("Grade 1");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState("Term 1");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  useEffect(() => {
    schoolSystemApi.listStudents(schoolId, grade)
      .then((r) => { setStudents(r.items || []); setStudentId(""); })
      .catch(() => setStudents([]));
  }, [schoolId, grade]);
  const pdfUrl = studentId
    ? schoolSystemApi.reportCardPdfUrl(schoolId, studentId, year, term)
    : null;
  return (
    <div className="space-y-6" data-testid="print-report-panel">
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Broadcast size={20} weight="fill" className="text-sky-400" /> Print / Save PDF report card
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-white/70">
            Open any student's report card as a PDF — the same document parents receive when you publish.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SelectField label="Grade" testId="print-grade" value={grade} onChange={setGrade} options={GRADES.map(g=>({value:g,label:g}))}/>
            <F label="Academic year"><Input data-testid="print-year" value={year} onChange={(e)=>setYear(e.target.value)} className="bg-white/5 border-white/10"/></F>
            <F label="Term"><Input data-testid="print-term" value={term} onChange={(e)=>setTerm(e.target.value)} className="bg-white/5 border-white/10"/></F>
          </div>
          <SelectField
            label="Student"
            testId="print-student"
            value={studentId}
            onChange={setStudentId}
            options={students.map((s) => ({ value: s.student_id, label: `${s.student_name} (${s.student_id})` }))}
          />
          {pdfUrl ? (
            <div className="pt-2">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ht-download-report-pdf"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm"
              >
                <PaperPlaneTilt size={14} weight="fill" /> Open / print PDF
              </a>
            </div>
          ) : (
            <p className="text-xs text-white/50">Pick a student to open the PDF.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



/* =====================================================================
 *  PROMOTE PANEL — individual, bulk, and whole-class promotion
 * ===================================================================== */
function PromotePanel({ schoolId }) {
  const [grade, setGrade] = useState("Grade 1");
  const [targetGrade, setTargetGrade] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await schoolSystemApi.listStudents(schoolId, grade);
      setStudents(r.items || []);
      setSelected(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [schoolId, grade]);

  const toggle = (sid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    return next;
  });
  const toggleAll = () => setSelected((prev) =>
    prev.size === students.length ? new Set() : new Set(students.map((s) => s.student_id))
  );

  const promoteOne = async (s) => {
    const dst = targetGrade || `next grade after ${s.grade}`;
    if (!window.confirm(`Promote ${s.student_name} from ${s.grade} to ${dst}?`)) return;
    setBusy(true);
    try {
      const r = await schoolSystemApi.promoteStudent({
        school_id: schoolId, student_id: s.student_id,
        academic_year: year, target_grade: targetGrade || null,
      });
      toast.success(`Promoted → ${r.to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const promoteBulk = async () => {
    if (selected.size === 0) return toast.error("Select at least one student");
    if (!window.confirm(`Promote ${selected.size} student(s) from ${grade}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await schoolSystemApi.promoteStudentsBulk({
        school_id: schoolId, student_ids: [...selected],
        academic_year: year, target_grade: targetGrade || null,
      });
      setResult(r);
      toast.success(`Promoted ${r.promoted} of ${r.total}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const promoteClass = async () => {
    if (!window.confirm(`Promote EVERY active student in ${grade}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await schoolSystemApi.promoteClass({
        school_id: schoolId, grade, academic_year: year,
        target_grade: targetGrade || null,
      });
      setResult(r);
      toast.success(`Class promoted → ${r.target_grade} (${r.promoted}/${r.total})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="promote-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SelectField label="Source grade" testId="promote-grade" value={grade} onChange={setGrade} options={GRADES.map(g=>({value:g,label:g}))}/>
        <SelectField label="Target grade (leave empty = auto next)" testId="promote-target" value={targetGrade || "__auto__"} onChange={(v) => setTargetGrade(v === "__auto__" ? "" : v)}
          options={[{value:"__auto__",label:"Auto (next in ladder)"}, ...GRADES.map(g=>({value:g,label:g}))]}/>
        <F label="Academic year"><Input data-testid="promote-year" value={year} onChange={(e)=>setYear(e.target.value)} className="bg-white/5 border-white/10"/></F>
      </div>
      <div className="flex flex-wrap gap-3 pt-2 pb-4 border-b border-white/10 action-bar">
        <Button data-testid="promote-bulk-btn" onClick={promoteBulk} disabled={busy || selected.size===0}
          className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
          Promote selected ({selected.size})
        </Button>
        <Button data-testid="promote-class-btn" onClick={promoteClass} disabled={busy || students.length===0}
          className="bg-fuchsia-500 hover:bg-fuchsia-600 text-black font-bold">
          Promote entire {grade}
        </Button>
        <Button variant="outline" onClick={toggleAll}
          className="border-white/20 bg-transparent hover:bg-white/5">
          {selected.size === students.length ? "Clear selection" : "Select all"}
        </Button>
      </div>
      <div className="space-y-3">
        <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Students in {grade}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {students.length === 0 && <p className="text-white/50 text-sm">No students in this grade.</p>}
          {students.map((s) => (
            <Card key={s.student_id} className="bg-[#121212] border-white/10" data-testid={`promote-row-${s.student_id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <label className="flex items-center gap-3 flex-1 cursor-pointer min-w-0">
                  <input
                    type="checkbox"
                    checked={selected.has(s.student_id)}
                    onChange={() => toggle(s.student_id)}
                    data-testid={`promote-check-${s.student_id}`}
                  />
                  <div className="min-w-0">
                    <p className="font-heading font-bold truncate">{s.student_name}</p>
                    <p className="text-xs text-white/50 truncate">{s.student_id} · {s.grade}</p>
                  </div>
                </label>
                <Button size="sm" onClick={() => promoteOne(s)} disabled={busy}
                  data-testid={`promote-one-${s.student_id}`}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
                  Promote
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {result && (
        <Card className="bg-white/[0.03] border-white/10 mt-4">
          <CardContent className="p-4 text-sm" data-testid="promote-result">
            <p><b>Total:</b> {result.total} · <b>Promoted:</b> <span className="text-emerald-300">{result.promoted}</span> · <b>Failed:</b> <span className="text-red-300">{result.failed}</span></p>
            {result.results?.filter((r)=>r.status!=="promoted").slice(0,5).map((r,i)=>(
              <p key={i} className="text-[11px] text-white/60">· {r.student_id}: {r.detail || r.status}</p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/* =====================================================================
 *  GRADING POLICY PANEL — per-school override of the auto engine
 * ===================================================================== */
function GradingPolicyPanel({ schoolId }) {
  const [policy, setPolicy] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [subjectRows, setSubjectRows] = useState([]);
  const [overallRows, setOverallRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await schoolSystemApi.getGradingPolicy(schoolId);
      setPolicy(r.policy);
      setDefaults(r.defaults);
      const seed = r.policy || r.defaults;
      setSubjectRows((seed.subject_table || []).map((x) => ({ ...x })));
      setOverallRows((seed.overall_table || []).map((x) => ({ ...x })));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [schoolId]);

  const save = async () => {
    setSaving(true);
    try {
      await schoolSystemApi.saveGradingPolicy({
        school_id: schoolId,
        subject_table: subjectRows.map((r) => ({
          min: Number(r.min) || 0, grade: r.grade || "", descriptor: r.descriptor || "",
        })),
        overall_table: overallRows.map((r) => ({
          min: Number(r.min) || 0, descriptor: r.descriptor || "",
          class_teacher_remark: r.class_teacher_remark || "",
          headteacher_remark: r.headteacher_remark || "",
        })),
      });
      toast.success("Grading policy saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset to defaults? Your override will be removed.")) return;
    try {
      await schoolSystemApi.resetGradingPolicy(schoolId);
      toast.success("Reverted to defaults");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setSub = (i, k, v) => setSubjectRows((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const setOvr = (i, k, v) => setOverallRows((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  if (!defaults) return <p className="text-white/50 text-sm">Loading…</p>;
  return (
    <div className="space-y-8" data-testid="grading-policy-panel">
      <div className="pb-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2 action-bar">
          <p className="text-sm text-white/70">
            {policy ? "Using a custom policy" : "Using default policy — save changes to override."}
          </p>
          <div className="flex gap-2">
            {policy && <Button size="sm" variant="outline" onClick={reset} className="border-white/20 bg-transparent hover:bg-white/5">Reset to defaults</Button>}
            <Button size="sm" onClick={save} disabled={saving} data-testid="grading-policy-save"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
              {saving ? "Saving…" : "Save policy"}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg mb-4">Subject grade table</h3>
        <div className="space-y-2">
          {subjectRows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`policy-subj-${i}`}>
              <Input type="number" value={r.min} onChange={(e)=>setSub(i,"min",e.target.value)} placeholder="Min" className="col-span-2 bg-white/5 border-white/10"/>
              <Input value={r.grade} onChange={(e)=>setSub(i,"grade",e.target.value)} placeholder="Grade" className="col-span-2 bg-white/5 border-white/10"/>
              <Input value={r.descriptor} onChange={(e)=>setSub(i,"descriptor",e.target.value)} placeholder="Descriptor" className="col-span-8 bg-white/5 border-white/10"/>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg mb-4">Overall performance + remarks</h3>
        <div className="space-y-4">
          {overallRows.map((r, i) => (
            <Card key={i} className="bg-[#121212] border-white/10" data-testid={`policy-ovr-${i}`}>
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-12 gap-2">
                  <Input type="number" value={r.min} onChange={(e)=>setOvr(i,"min",e.target.value)} placeholder="Min avg" className="col-span-2 bg-white/5 border-white/10"/>
                  <Input value={r.descriptor} onChange={(e)=>setOvr(i,"descriptor",e.target.value)} placeholder="Overall descriptor" className="col-span-10 bg-white/5 border-white/10"/>
                </div>
                <Textarea rows={2} value={r.class_teacher_remark} onChange={(e)=>setOvr(i,"class_teacher_remark",e.target.value)} placeholder="Class teacher's remark" className="bg-white/5 border-white/10"/>
                <Textarea rows={2} value={r.headteacher_remark} onChange={(e)=>setOvr(i,"headteacher_remark",e.target.value)} placeholder="Headteacher / Principal's remark" className="bg-white/5 border-white/10"/>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}


/* =====================================================================
 *  Phase 3c panels: Admission Register, Master Fees, School Calendar
 * ===================================================================== */

function AdmissionRegisterPanel({ schoolId }) {
  const [rows, setRows] = useState([]);
  const [grade, setGrade] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (grade) params.grade = grade;
      if (year) params.academic_year = year;
      const r = await schoolSystemApi.admissionRegister(schoolId, params);
      setRows(r.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);
  return (
    <div className="space-y-4" data-testid="ht-admission-register">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[160px]">
          <SelectField label="Grade" value={grade || "__all__"} onChange={v => setGrade(v === "__all__" ? "" : v)}
            options={[{ value: "__all__", label: "All grades" }, ...GRADES.map(g => ({ value: g, label: g }))]} />
        </div>
        <div>
          <label className="text-xs text-white/50 mb-1 block">Academic year</label>
          <Input value={year} onChange={e => setYear(e.target.value)} placeholder="2026"
                 className="bg-white/5 border-white/10" data-testid="ht-register-year"/>
        </div>
        <Button onClick={load} data-testid="ht-register-refresh">Refresh</Button>
        <Button
          onClick={() => {
            const qp = new URLSearchParams({ school_id: schoolId });
            if (grade) qp.set("grade", grade);
            if (year) qp.set("academic_year", year);
            downloadPdf(`/school-system/admission-register/pdf?${qp}`,
                         `admission-register-${grade || "all"}-${year || ""}.pdf`)
              .catch((e) => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
          }}
          data-testid="ht-register-pdf"
          className="bg-sky-500 hover:bg-sky-600 text-black font-bold">
          Generate PDF
        </Button>
        <p className="text-xs text-white/40">Admission numbers are auto-generated and cannot be edited.</p>
      </div>
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">
          Admission register — {rows.length} student{rows.length === 1 ? "" : "s"}
        </CardTitle></CardHeader>
        <CardContent>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && rows.length === 0 && <p className="text-white/50 text-sm">No students match the filters.</p>}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-white/50 uppercase text-xs">
                  <tr>
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Admission #</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Grade</th>
                    <th className="py-2 pr-3">Gender</th>
                    <th className="py-2 pr-3">DOB</th>
                    <th className="py-2 pr-3">Parent</th>
                    <th className="py-2 pr-3">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.admission_number} className="border-t border-white/5" data-testid={`ht-register-row-${r.admission_number}`}>
                      <td className="py-2 pr-3 text-white/50">{r.serial}</td>
                      <td className="py-2 pr-3 font-mono">{r.admission_number}</td>
                      <td className="py-2 pr-3">{r.student_name}</td>
                      <td className="py-2 pr-3">{r.grade}</td>
                      <td className="py-2 pr-3">{r.gender || "—"}</td>
                      <td className="py-2 pr-3">{r.date_of_birth || "—"}</td>
                      <td className="py-2 pr-3">{r.parent_name || "—"}</td>
                      <td className="py-2 pr-3">{r.parent_contact || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MasterFeesPanel({ schoolId }) {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fee_type: "", grade: "", academic_year: String(new Date().getFullYear()),
    term: "Term 1", amount_due: "", description: "", payment_instructions: "",
  });
  // Ledger PDF filters — allow HT to print one class / term at a time.
  const [ledger, setLedger] = useState({
    grade: "", term: "", academic_year: "",
  });
  const load = async () => {
    setLoading(true);
    try {
      const r = await schoolSystemApi.listFeeTemplates(schoolId);
      setTpls(r.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);
  const create = async () => {
    try {
      await schoolSystemApi.createFeeTemplate({
        school_id: schoolId,
        fee_type: form.fee_type,
        grade: form.grade || null,
        academic_year: form.academic_year,
        term: form.term,
        amount_due: Number(form.amount_due || 0),
        description: form.description,
        payment_instructions: form.payment_instructions,
      });
      toast.success("Template saved");
      setForm({ ...form, fee_type: "", amount_due: "", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async id => {
    if (!window.confirm("Delete this fee template?")) return;
    try { await schoolSystemApi.deleteFeeTemplate(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const apply = async (tpl) => {
    if (!window.confirm(`Apply "${tpl.fee_type}" to every active student in ${tpl.grade || "all grades"}?`)) return;
    try {
      const r = await schoolSystemApi.applyFeeTemplate(tpl.template_id);
      toast.success(`Applied to ${r.applied} students (${r.skipped} already had it).`);
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
  };
  return (
    <div className="space-y-4" data-testid="ht-master-fees">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Create fee template</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <F label="Fee type"><Input data-testid="fee-tpl-type" value={form.fee_type} onChange={e => setForm({...form, fee_type: e.target.value})} placeholder="Tuition / Feeding / Uniform"/></F>
          <F label="Amount (GHS)"><Input data-testid="fee-tpl-amount" type="number" value={form.amount_due} onChange={e => setForm({...form, amount_due: e.target.value})}/></F>
          <SelectField label="Grade (blank = all)" value={form.grade || "__all__"} onChange={v => setForm({...form, grade: v === "__all__" ? "" : v})}
                        options={[{ value: "__all__", label: "All grades" }, ...GRADES.map(g => ({ value: g, label: g }))]} />
          <F label="Academic year"><Input data-testid="fee-tpl-year" value={form.academic_year} onChange={e => setForm({...form, academic_year: e.target.value})}/></F>
          <SelectField label="Term" value={form.term} onChange={v => setForm({...form, term: v})}
                        options={["Term 1","Term 2","Term 3"].map(t => ({ value: t, label: t }))} />
          <F label="Description"><Input data-testid="fee-tpl-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})}/></F>
          <div className="md:col-span-3">
            <F label="Payment instructions">
              <Textarea data-testid="fee-tpl-instructions" rows={2} value={form.payment_instructions} onChange={e => setForm({...form, payment_instructions: e.target.value})}
                         placeholder="Momo, bank details, when due, etc."/>
            </F>
          </div>
          <div className="md:col-span-3">
            <Button onClick={create} data-testid="fee-tpl-create" disabled={!form.fee_type || !form.amount_due}>Save template</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading">Templates ({tpls.length})</CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-32">
              <SelectField label="Grade" value={ledger.grade || "__all__"}
                            onChange={v => setLedger(l => ({...l, grade: v === "__all__" ? "" : v}))}
                            options={[{value:"__all__",label:"All grades"}, ...GRADES.map(g=>({value:g,label:g}))]}/>
            </div>
            <div className="w-28">
              <SelectField label="Term" value={ledger.term || "__all__"}
                            onChange={v => setLedger(l => ({...l, term: v === "__all__" ? "" : v}))}
                            options={[{value:"__all__",label:"All terms"}, ...["Term 1","Term 2","Term 3"].map(t=>({value:t,label:t}))]}/>
            </div>
            <div className="w-24">
              <label className="text-xs text-white/50 mb-1 block">Year</label>
              <Input value={ledger.academic_year}
                     onChange={e => setLedger(l => ({...l, academic_year: e.target.value}))}
                     className="bg-white/5 border-white/10" placeholder="2026"
                     data-testid="ht-ledger-year"/>
            </div>
            <Button
              onClick={() => {
                const qp = new URLSearchParams({ school_id: schoolId });
                if (ledger.grade) qp.set("grade", ledger.grade);
                if (ledger.term) qp.set("term", ledger.term);
                if (ledger.academic_year) qp.set("academic_year", ledger.academic_year);
                downloadPdf(`/school-system/fees/report/pdf?${qp}`,
                             `fees-ledger-${ledger.grade || "all"}-${ledger.term || ""}.pdf`)
                  .catch((e) => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
              }}
              data-testid="ht-fees-ledger-pdf"
              className="bg-sky-500 hover:bg-sky-600 text-black font-bold">
              Generate Ledger PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && tpls.length === 0 && <p className="text-white/50 text-sm">No templates yet.</p>}
          <div className="space-y-2">
            {tpls.map(t => (
              <div key={t.template_id} className="p-3 rounded-md bg-white/[0.02] border border-white/5 flex items-center justify-between gap-3" data-testid={`fee-tpl-${t.template_id}`}>
                <div className="text-sm">
                  <p className="font-semibold">{t.fee_type} — GHS {t.amount_due}</p>
                  <p className="text-white/50 text-xs">{t.grade || "All grades"} • {t.academic_year} {t.term}</p>
                  {t.description && <p className="text-white/50 text-xs mt-1">{t.description}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => apply(t)} data-testid={`fee-tpl-apply-${t.template_id}`}>Apply</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t.template_id)} className="text-red-400" data-testid={`fee-tpl-del-${t.template_id}`}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FEE ENTRIES  — per-student fee entry with arrears + running balance +
 * instant receipt PDF. HT-only. Lives inside the Master Fees section.
 * ------------------------------------------------------------------------- */
function FeeEntriesPanel({ schoolId }) {
  const currentYear = String(new Date().getFullYear());
  const [filters, setFilters] = useState({
    grade: "", academic_year: currentYear, term: "Term 1",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [feeType, setFeeType] = useState("Tuition");
  const [feeForms, setFeeForms] = useState({});
  const [payForms, setPayForms] = useState({});
  const [sendingReminders, setSendingReminders] = useState(false);
  const [lastReminder, setLastReminder] = useState(null);

  const load = async () => {
    if (!filters.grade) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await schoolSystemApi.htFeesByClass(schoolId, {
        grade: filters.grade,
        academic_year: filters.academic_year,
        term: filters.term,
      });
      setRows(r.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ },
            [schoolId, filters.grade, filters.academic_year, filters.term]);

  const getFeeFor = (item) =>
    (item.fees || []).find(f => (f.fee_type || "").toLowerCase() === feeType.toLowerCase());

  const setFeeField = (sid, k, v) =>
    setFeeForms(m => ({ ...m, [sid]: { ...(m[sid] || {}), [k]: v } }));
  const setPayField = (sid, k, v) =>
    setPayForms(m => ({ ...m, [sid]: { ...(m[sid] || {}), [k]: v } }));

  const upsertFee = async (item) => {
    const sid = item.student.student_id;
    const form = feeForms[sid] || {};
    const existing = getFeeFor(item);
    const amount_due = Number(form.amount_due ?? existing?.amount_due ?? 0);
    const arrears = Number(form.arrears ?? existing?.arrears ?? 0);
    if (!feeType.trim()) return toast.error("Enter a fee type first");
    try {
      await schoolSystemApi.htUpsertFee({
        school_id: schoolId,
        student_id: sid,
        fee_type: feeType.trim(),
        academic_year: filters.academic_year,
        term: filters.term,
        amount_due, arrears,
        description: form.description || "",
      });
      toast.success(`Fee saved for ${item.student.student_name}`);
      setFeeForms(m => ({ ...m, [sid]: {} }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const recordPayment = async (item) => {
    const sid = item.student.student_id;
    const fee = getFeeFor(item);
    if (!fee) return toast.error(`Set the "${feeType}" fee first`);
    const form = payForms[sid] || {};
    const amount = Number(form.amount || 0);
    if (!(amount > 0)) return toast.error("Enter a positive amount");
    try {
      const r = await schoolSystemApi.issueReceipt(fee.fee_id, {
        amount, method: form.method || "cash", note: form.note || "",
      });
      toast.success(`Recorded GHS ${amount} · Receipt ${r.receipt.receipt_number}`);
      const qp = new URLSearchParams({ receipt_id: r.receipt.receipt_id });
      downloadPdf(`/school-system/fees/${fee.fee_id}/receipt/pdf?${qp}`,
                   `receipt-${r.receipt.receipt_number}.pdf`)
        .catch((e) => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
      setPayForms(m => ({ ...m, [sid]: {} }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
  };

  const openReceiptsPdf = (fee) => {
    downloadPdf(`/school-system/fees/${fee.fee_id}/receipt/pdf`,
                 `receipts-${fee.fee_id}.pdf`)
      .catch((e) => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
  };

  const sendReminders = async () => {
    // Preview first so the HT sees the size of the blast
    try {
      const preview = await schoolSystemApi.htFeeRemindPreview(schoolId, {
        grade: filters.grade || undefined,
        academic_year: filters.academic_year || undefined,
        term: filters.term || undefined,
      });
      if (preview.students_targeted === 0) {
        toast.info("No parents to remind — every student has zero balance.");
        return;
      }
      const confirmed = window.confirm(
        `Send fees reminder to ${preview.unique_parent_emails} parent email` +
        `${preview.unique_parent_emails === 1 ? "" : "s"} covering ` +
        `${preview.students_targeted} student` +
        `${preview.students_targeted === 1 ? "" : "s"} ` +
        `(total balance GHS ${preview.total_outstanding.toFixed(2)})?\n\n` +
        `Each parent will receive a friendly email with the student's ` +
        `outstanding statement attached as a PDF.`
      );
      if (!confirmed) return;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      return;
    }
    const note = window.prompt(
      "Optional friendly note to include in every email (or leave blank):",
      ""
    );
    setSendingReminders(true);
    try {
      const r = await schoolSystemApi.htSendFeeReminder({
        school_id: schoolId,
        grade: filters.grade || null,
        academic_year: filters.academic_year || null,
        term: filters.term || null,
        min_balance: 0.01,
        note: note || "",
        send_email: true,
      });
      setLastReminder(r);
      toast.success(
        `Reminders sent · ${r.emailed} email${r.emailed === 1 ? "" : "s"}` +
        (r.notified ? ` · ${r.notified} in-app notification${r.notified === 1 ? "" : "s"}` : "") +
        (r.email_failures?.length ? ` · ${r.email_failures.length} failure${r.email_failures.length === 1 ? "" : "s"}` : "")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reminder blast failed");
    } finally {
      setSendingReminders(false);
    }
  };

  const totals = rows.reduce((acc, r) => {
    acc.owed += r.totals?.owed || 0;
    acc.paid += r.totals?.paid || 0;
    acc.outstanding += r.totals?.outstanding || 0;
    return acc;
  }, { owed: 0, paid: 0, outstanding: 0 });

  return (
    <div className="space-y-4" data-testid="ht-fee-entries">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Enter fees per student</CardTitle>
          <p className="text-white/50 text-xs mt-1">
            Pick a class, term and fee type — then set each student's term fee, carry-forward
            arrears and record payments. Balance recalculates automatically. Every payment
            produces a professional PDF receipt.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <SelectField label="Class / Level"
                        value={filters.grade || "__pick__"}
                        onChange={v => setFilters(f => ({...f, grade: v === "__pick__" ? "" : v}))}
                        options={[{value:"__pick__", label:"Select class…"},
                                  ...GRADES.map(g => ({ value: g, label: g }))]} />
          <F label="Academic year">
            <Input data-testid="fee-entries-year"
                   value={filters.academic_year}
                   onChange={e => setFilters(f => ({...f, academic_year: e.target.value}))}
                   className="bg-white/5 border-white/10"/>
          </F>
          <SelectField label="Term" value={filters.term}
                        onChange={v => setFilters(f => ({...f, term: v}))}
                        options={["Term 1","Term 2","Term 3"].map(t => ({ value: t, label: t }))} />
          <F label="Fee type">
            <Input data-testid="fee-entries-type" value={feeType}
                   onChange={e => setFeeType(e.target.value)}
                   placeholder="Tuition / Feeding / Uniform"
                   className="bg-white/5 border-white/10"/>
          </F>
        </CardContent>
      </Card>

      {filters.grade && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-heading">
              {filters.grade} · {filters.term} · {filters.academic_year}
              <span className="text-xs font-normal text-white/50 ml-2">
                ({rows.length} student{rows.length === 1 ? "" : "s"})
              </span>
            </CardTitle>
            <div className="text-xs flex gap-3 flex-wrap items-center">
              <span className="text-white/60">Owed <b className="text-white">GHS {totals.owed.toFixed(2)}</b></span>
              <span className="text-emerald-400">Paid <b>GHS {totals.paid.toFixed(2)}</b></span>
              <span className="text-amber-400">Balance <b>GHS {totals.outstanding.toFixed(2)}</b></span>
              <Button size="sm" variant="outline"
                onClick={() => {
                  const qp = new URLSearchParams({ school_id: schoolId,
                    grade: filters.grade, term: filters.term,
                    academic_year: filters.academic_year });
                  downloadPdf(`/school-system/fees/report/pdf?${qp}`,
                               `fees-ledger-${filters.grade}-${filters.term}.pdf`)
                    .catch((e) => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
                }}
                data-testid="fee-entries-ledger-pdf">
                Ledger PDF
              </Button>
              <Button size="sm"
                onClick={sendReminders}
                disabled={sendingReminders}
                data-testid="fee-entries-send-reminders"
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                {sendingReminders ? "Sending…" : "Remind parents"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {lastReminder && (
              <div className="mb-3 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-sm"
                    data-testid="fee-entries-last-reminder">
                Last blast · <b>{lastReminder.emailed}</b> email
                {lastReminder.emailed === 1 ? "" : "s"} sent to
                {" "}<b>{lastReminder.students_targeted}</b> student
                {lastReminder.students_targeted === 1 ? "" : "s"}
                {lastReminder.notified ? <> · <b>{lastReminder.notified}</b> in-app</> : null}
                {lastReminder.email_failures?.length
                  ? <span className="text-amber-300"> · {lastReminder.email_failures.length} failure(s)</span>
                  : null}
              </div>
            )}
            {loading && <p className="text-white/60 text-sm">Loading…</p>}
            {!loading && rows.length === 0 && (
              <p className="text-white/50 text-sm">No active students in this class.</p>
            )}
            <div className="space-y-2">
              {rows.map(item => {
                const sid = item.student.student_id;
                const existing = getFeeFor(item);
                const paid = Number(existing?.amount_paid || 0);
                const due = Number(existing?.amount_due || 0);
                const arr = Number(existing?.arrears || 0);
                const owed = due + arr;
                const balance = Math.max(owed - paid, 0);
                const isOpen = expanded === sid;
                const form = feeForms[sid] || {};
                const pay = payForms[sid] || {};
                return (
                  <div key={sid}
                       className="p-3 rounded-md bg-white/[0.02] border border-white/5"
                       data-testid={`fee-row-${sid}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-sm">
                        <p className="font-semibold">
                          {item.student.student_name}
                          <span className="text-white/40 text-xs ml-2">{sid}</span>
                        </p>
                        <p className="text-white/50 text-xs">
                          {feeType}: owed GHS {owed.toFixed(2)} · paid GHS {paid.toFixed(2)} ·
                          <span className={balance > 0 ? " text-amber-400" : " text-emerald-400"}>
                            &nbsp;balance GHS {balance.toFixed(2)}
                          </span>
                        </p>
                      </div>
                      <Button size="sm" variant="outline"
                              data-testid={`fee-toggle-${sid}`}
                              onClick={() => setExpanded(isOpen ? null : sid)}>
                        {isOpen ? "Close" : "Enter / Pay"}
                      </Button>
                    </div>

                    {isOpen && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-3 rounded-md bg-black/30 border border-white/5 space-y-2">
                          <p className="text-xs uppercase text-white/50 tracking-wider">1 · Set term fee & arrears</p>
                          <div className="grid grid-cols-2 gap-2">
                            <F label={`Term fee (GHS) · ${feeType}`}>
                              <Input type="number" step="0.01"
                                data-testid={`fee-due-${sid}`}
                                value={form.amount_due ?? (existing?.amount_due ?? "")}
                                onChange={e => setFeeField(sid, "amount_due", e.target.value)}
                                className="bg-white/5 border-white/10"/>
                            </F>
                            <F label="Arrears b/f (GHS)">
                              <Input type="number" step="0.01"
                                data-testid={`fee-arr-${sid}`}
                                value={form.arrears ?? (existing?.arrears ?? "")}
                                onChange={e => setFeeField(sid, "arrears", e.target.value)}
                                className="bg-white/5 border-white/10"/>
                            </F>
                          </div>
                          <F label="Note (optional)">
                            <Input data-testid={`fee-desc-${sid}`}
                              value={form.description ?? (existing?.description ?? "")}
                              onChange={e => setFeeField(sid, "description", e.target.value)}
                              className="bg-white/5 border-white/10"
                              placeholder="e.g. includes PTA levy"/>
                          </F>
                          <Button size="sm" onClick={() => upsertFee(item)}
                                  data-testid={`fee-save-${sid}`}
                                  className="bg-sky-500 hover:bg-sky-600 text-black font-bold">
                            Save fee
                          </Button>
                        </div>

                        <div className="p-3 rounded-md bg-black/30 border border-white/5 space-y-2">
                          <p className="text-xs uppercase text-white/50 tracking-wider">2 · Record payment</p>
                          <div className="grid grid-cols-2 gap-2">
                            <F label="Amount (GHS)">
                              <Input type="number" step="0.01"
                                data-testid={`pay-amt-${sid}`}
                                value={pay.amount || ""}
                                onChange={e => setPayField(sid, "amount", e.target.value)}
                                className="bg-white/5 border-white/10"/>
                            </F>
                            <SelectField label="Method"
                              value={pay.method || "cash"}
                              onChange={v => setPayField(sid, "method", v)}
                              options={["cash","momo","bank","cheque","card"].map(v => ({value:v, label:v}))}/>
                          </div>
                          <F label="Note (optional)">
                            <Input data-testid={`pay-note-${sid}`}
                              value={pay.note || ""}
                              onChange={e => setPayField(sid, "note", e.target.value)}
                              className="bg-white/5 border-white/10"
                              placeholder="e.g. part payment"/>
                          </F>
                          <div className="flex gap-2 flex-wrap">
                            <Button size="sm" onClick={() => recordPayment(item)}
                                    data-testid={`pay-submit-${sid}`}
                                    disabled={!existing}
                                    className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
                              Record & print receipt
                            </Button>
                            {existing && (
                              <Button size="sm" variant="outline"
                                      data-testid={`pay-history-pdf-${sid}`}
                                      onClick={() => openReceiptsPdf(existing)}>
                                All receipts PDF
                              </Button>
                            )}
                          </div>
                          {!existing && (
                            <p className="text-xs text-amber-400/80">
                              Save the fee first, then record payments.
                            </p>
                          )}
                        </div>

                        {(item.fees || []).length > 0 && (
                          <div className="md:col-span-2 mt-1">
                            <p className="text-xs uppercase text-white/50 tracking-wider mb-2">
                              All fee entries for {item.student.student_name}
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="text-white/50">
                                  <tr><th className="text-left p-2">Fee</th>
                                      <th className="text-right p-2">Term Fee</th>
                                      <th className="text-right p-2">Arrears</th>
                                      <th className="text-right p-2">Paid</th>
                                      <th className="text-right p-2">Balance</th>
                                      <th className="text-right p-2"></th></tr>
                                </thead>
                                <tbody>
                                  {(item.fees || []).map(f => {
                                    const _due = Number(f.amount_due || 0);
                                    const _arr = Number(f.arrears || 0);
                                    const _paid = Number(f.amount_paid || 0);
                                    const _bal = Math.max((_due + _arr) - _paid, 0);
                                    return (
                                      <tr key={f.fee_id} className="border-t border-white/5">
                                        <td className="p-2">{f.fee_type}</td>
                                        <td className="p-2 text-right">{_due.toFixed(2)}</td>
                                        <td className="p-2 text-right">{_arr.toFixed(2)}</td>
                                        <td className="p-2 text-right text-emerald-400">{_paid.toFixed(2)}</td>
                                        <td className={"p-2 text-right " + (_bal > 0 ? "text-amber-400" : "text-emerald-400")}>{_bal.toFixed(2)}</td>
                                        <td className="p-2 text-right">
                                          <Button size="sm" variant="ghost"
                                                  className="h-6 px-2"
                                                  onClick={() => openReceiptsPdf(f)}
                                                  data-testid={`fee-receipt-pdf-${f.fee_id}`}>
                                            Receipts
                                          </Button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}



/* ---------------------------------------------------------------------------
 * APPROVED HISTORY  — read-only archive of every item the HT has approved
 * across tests, marking schemes, student results, lesson notes,
 * attendance, behaviour, homework, fees and parent messages, each with
 * a printable PDF where available.
 * ------------------------------------------------------------------------- */
const APPROVED_KINDS = [
  { value: "",                label: "All" },
  { value: "lesson_note",     label: "Lesson notes" },
  { value: "test",            label: "Tests" },
  { value: "marking_scheme",  label: "Marking schemes" },
  { value: "student_results", label: "Student results" },
  { value: "record",          label: "Records" },
  { value: "attendance",      label: "Attendance" },
  { value: "behaviour",       label: "Behaviour" },
  { value: "homework",        label: "Homework" },
  { value: "fee",             label: "Fees" },
  { value: "parent_message",  label: "Parent messages" },
];

function ApprovedHistoryPanel({ schoolId }) {
  const [kind, setKind] = useState("");
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (kind) params.kind = kind;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (gradeFilter) params.grade = gradeFilter;
      const r = await schoolSystemApi.htApprovalsHistory(schoolId, params);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (schoolId) load(); /* eslint-disable-next-line */ },
            [schoolId, kind, dateFrom, dateTo, gradeFilter]);

  const rowTitle = (it) => {
    const t = it.target || it;
    switch (it._kind) {
      case "lesson_note":     return t.title || "Lesson note";
      case "test":            return t.content?.title || `${t.subject || ""} · ${t.topic || "test"}`;
      case "marking_scheme":  return `Scheme · ${it.target?.subject || t.subject || t.test_id}`;
      case "student_results": return `${t.test_title || t.subject || "Result"} · ${it._student_name || t.student_id}`;
      case "record":          return `${t.record_type || "Record"} · ${it._student_name || t.student_id || ""}`;
      case "attendance":      return `Attendance · ${it._student_name || t.student_id || ""} · ${(t.date || "").slice(0,10)}`;
      case "behaviour":       return `Behaviour · ${it._student_name || t.student_id || ""}`;
      case "homework":        return `Homework · ${t.title || t.subject || ""}`;
      case "fee":             return `Fee · ${it._student_name || t.student_id || ""} · GHS ${(t.amount_due || 0)}`;
      case "parent_message":  return `Message · ${(t.subject || t.body || "").slice(0, 60)}`;
      default:                return it._kind;
    }
  };
  const rowSub = (it) => {
    const bits = [];
    if (it._grade || it.target?.grade) bits.push(it._grade || it.target?.grade);
    if (it._subject_name) bits.push(it._subject_name);
    if (it._by) bits.push(`by ${it._by}`);
    if (it._when) bits.push((it._when || "").slice(0, 16).replace("T", " "));
    return bits.join(" · ");
  };

  const printPdf = (it) => {
    if (!it._pdf_url) return;
    // pdf_url starts with `/school-system/...`; strip that prefix so the
    // shared downloader (which prefixes /api) resolves correctly.
    const path = it._pdf_url.startsWith("/school-system/")
      ? it._pdf_url : it._pdf_url;
    downloadPdf(path, `${it._kind}-${it._target_id}.pdf`)
      .catch(e => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
  };

  return (
    <div className="space-y-4" data-testid="ht-approved-history">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Approved history</CardTitle>
          <p className="text-white/50 text-xs mt-1">
            Everything you have approved so far, newest first. Tap "Print PDF"
            to download a Unicode-safe printable copy where one is available.
          </p>
        </CardHeader>
        <CardContent>
          <Input
            data-testid="approved-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search students, subjects, topics, decision notes…"
            className="bg-white/5 border-white/10 mb-3"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">
                From date
              </label>
              <Input type="date" value={dateFrom}
                     data-testid="ht-approved-date-from"
                     onChange={(e) => setDateFrom(e.target.value)}
                     className="bg-white/5 border-white/10"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">
                To date
              </label>
              <Input type="date" value={dateTo}
                     data-testid="ht-approved-date-to"
                     onChange={(e) => setDateTo(e.target.value)}
                     className="bg-white/5 border-white/10"/>
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">
                Grade
              </label>
              <select value={gradeFilter}
                      data-testid="ht-approved-grade"
                      onChange={(e) => setGradeFilter(e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-white/5 border border-white/10 text-sm">
                <option value="">All grades</option>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="col-span-2 md:col-span-1 flex items-end">
              <Button size="sm" variant="outline"
                      data-testid="ht-approved-clear-filters"
                      onClick={() => { setDateFrom(""); setDateTo(""); setGradeFilter(""); }}
                      className="w-full">
                Clear filters
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {APPROVED_KINDS.map(k => (
              <Button
                key={k.value || "all"}
                size="sm"
                variant={kind === k.value ? "default" : "outline"}
                onClick={() => setKind(k.value)}
                data-testid={`approved-filter-${k.value || "all"}`}
                className={kind === k.value ? "bg-emerald-500 text-black font-bold" : ""}
              >
                {k.label}
              </Button>
            ))}
          </div>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {(() => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? items.filter(it => {
                  const bag = [
                    rowTitle(it), rowSub(it),
                    it._student_name, it._subject_name, it._grade, it._by,
                    it.decision_note, it._target_id,
                    it.target?.subject, it.target?.topic, it.target?.title,
                    it.target?.body, it.target?.student_name,
                  ].filter(Boolean).join(" ").toLowerCase();
                  return bag.includes(q);
                })
              : items;
            if (!loading && filtered.length === 0) {
              return <p className="text-white/50 text-sm">
                {q ? `No approved items matching "${query}".` : "No approved items to show yet."}
              </p>;
            }
            return (
              <div className="space-y-2">
                {filtered.map((it, i) => (
                  <div key={`${it._kind}-${it._target_id}-${i}`}
                       className="p-3 rounded-md bg-white/[0.02] border border-white/5
                                  flex items-center justify-between gap-3 flex-wrap"
                       data-testid={`approved-row-${it._target_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">
                        <span className="text-emerald-300 text-[10px] uppercase tracking-widest mr-2">
                          {it._kind.replace(/_/g, " ")}
                        </span>
                        {rowTitle(it)}
                      </p>
                      <p className="text-white/40 text-xs">{rowSub(it)}</p>
                      {it.decision_note && (
                        <p className="text-white/50 text-[11px] italic mt-0.5">
                          note: {it.decision_note}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {it._pdf_url && (
                        <Button size="sm" variant="outline"
                                data-testid={`approved-pdf-${it._target_id}`}
                                onClick={() => printPdf(it)}>
                          Print PDF
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}


/* ---------------------------------------------------------------------------
 * PARENT BROADCAST  — HT sends one email to every connected parent inbox
 * ------------------------------------------------------------------------- */
function ParentBroadcastPanel({ schoolId }) {
  const [form, setForm] = useState({ subject: "", body: "", grade: "" });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadHistory = async () => {
    try {
      const r = await schoolSystemApi.htListBroadcasts(schoolId, 25);
      setHistory(r.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { if (schoolId) loadHistory(); /* eslint-disable-next-line */ }, [schoolId]);

  const send = async () => {
    if (!form.subject.trim()) return toast.error("Enter a subject");
    if (!form.body.trim()) return toast.error("Enter a message body");
    if (!window.confirm(
      `Send this message to every parent email on file` +
      (form.grade ? ` in ${form.grade}` : "") + "?"
    )) return;
    setSending(true);
    try {
      const r = await schoolSystemApi.htBroadcastParents({
        school_id: schoolId,
        subject: form.subject.trim(),
        body: form.body,
        grade: form.grade || null,
        only_active: true,
      });
      toast.success(
        r.recipients === 0
          ? "No parent emails on file yet."
          : `Sent to ${r.sent}/${r.recipients} parents` +
             (r.failures?.length ? ` · ${r.failures.length} failed` : "")
      );
      setForm({ subject: "", body: "", grade: "" });
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="ht-broadcast">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Broadcast to parents</CardTitle>
          <p className="text-white/50 text-xs mt-1">
            One tap sends your message to every parent inbox on file — both
            the primary parent email and every address a parent has typed
            into the portal entry gate.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <F label="Class filter (optional)">
            <SelectField label=""
                          value={form.grade || "__all__"}
                          onChange={v => set("grade", v === "__all__" ? "" : v)}
                          options={[{value:"__all__", label:"All classes"},
                                    ...GRADES.map(g => ({ value: g, label: g }))]} />
          </F>
          <F label="Subject">
            <Input data-testid="broadcast-subject"
                   value={form.subject}
                   onChange={e => set("subject", e.target.value)}
                   placeholder="e.g. Term reopening reminder"
                   className="bg-white/5 border-white/10"/>
          </F>
          <F label="Message">
            <Textarea data-testid="broadcast-body"
                       rows={8}
                       value={form.body}
                       onChange={e => set("body", e.target.value)}
                       placeholder="Dear parents…"
                       className="bg-white/5 border-white/10"/>
          </F>
          <div className="flex justify-end">
            <Button data-testid="broadcast-send-btn"
                     disabled={sending} onClick={send}
                     className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
              {sending ? "Sending…" : "Send broadcast"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardHeader><CardTitle className="font-heading text-lg">Recent broadcasts</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {history.map(h => (
                <div key={h.broadcast_id}
                     data-testid={`broadcast-row-${h.broadcast_id}`}
                     className="p-2 rounded-md bg-white/[0.02] border border-white/5 text-sm">
                  <p className="font-semibold truncate">{h.subject}</p>
                  <p className="text-white/40 text-xs">
                    {(h.sent_at || "").slice(0,16).replace("T", " ")}
                    {" · "}{h.grade || "all classes"}
                    {" · "}<b className="text-emerald-400">{h.sent}</b>/{h.recipients} sent
                    {h.failures?.length ? <span className="text-amber-300"> · {h.failures.length} failed</span> : null}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CalendarPanel({ schoolId }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [terms, setTerms] = useState([
    { term: "Term 1", reopening_date: "", closing_date: "", vacation_notes: "" },
    { term: "Term 2", reopening_date: "", closing_date: "", vacation_notes: "" },
    { term: "Term 3", reopening_date: "", closing_date: "", vacation_notes: "" },
  ]);
  const [loading, setLoading] = useState(false);
  const load = async (y) => {
    setLoading(true);
    try {
      const r = await schoolSystemApi.getCalendar(schoolId, y);
      const item = (r.items || [])[0];
      if (item && Array.isArray(item.terms) && item.terms.length) setTerms(item.terms);
      else setTerms([
        { term: "Term 1", reopening_date: "", closing_date: "", vacation_notes: "" },
        { term: "Term 2", reopening_date: "", closing_date: "", vacation_notes: "" },
        { term: "Term 3", reopening_date: "", closing_date: "", vacation_notes: "" },
      ]);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [schoolId]);
  const save = async () => {
    try {
      await schoolSystemApi.saveCalendar({ school_id: schoolId, academic_year: year, terms });
      toast.success("Calendar saved. Parents will see the new dates.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const setField = (i, k, v) => setTerms(ts => ts.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  return (
    <div className="space-y-4" data-testid="ht-calendar">
      <div className="flex flex-wrap items-end gap-3">
        <F label="Academic year">
          <Input value={year} onChange={e => setYear(e.target.value)} className="bg-white/5 border-white/10 w-32" data-testid="cal-year"/>
        </F>
        <Button variant="outline" onClick={() => load(year)} data-testid="cal-reload">Load</Button>
        <Button onClick={save} data-testid="cal-save">Save calendar</Button>
      </div>
      {loading && <p className="text-white/60 text-sm">Loading…</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {terms.map((t, i) => (
          <Card key={i} className="bg-white/[0.03] border-white/10" data-testid={`cal-term-${i}`}>
            <CardHeader><CardTitle className="font-heading text-lg">{t.term}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <F label="Reopening date">
                <Input type="date" value={t.reopening_date} onChange={e => setField(i, "reopening_date", e.target.value)} data-testid={`cal-open-${i}`}/>
              </F>
              <F label="Closing date">
                <Input type="date" value={t.closing_date} onChange={e => setField(i, "closing_date", e.target.value)} data-testid={`cal-close-${i}`}/>
              </F>
              <F label="Vacation notes">
                <Textarea rows={2} value={t.vacation_notes || ""} onChange={e => setField(i, "vacation_notes", e.target.value)} data-testid={`cal-notes-${i}`}/>
              </F>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}



/* ------------------------------------------------------------------ *
 *  Publish Performance — HT snapshots + notifies parents (in-app + email)
 * ------------------------------------------------------------------ */
function PublishPerformancePanel({ schoolId }) {
  const [form, setForm] = useState({
    academic_year: String(new Date().getFullYear()),
    term: "Term 1",
    grade: "",           // "" => all students
    send_email: true,
    note: "",
  });
  const [publishing, setPublishing] = useState(false);
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await schoolSystemApi.listPerformancePublications(schoolId);
      setPubs(r.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);

  const publish = async () => {
    if (!window.confirm(
      `This will notify every parent whose child has approved results. Continue?`
    )) return;
    setPublishing(true);
    try {
      let student_ids = null;
      if (form.grade) {
        const roster = await schoolSystemApi.listStudentsForGrade(schoolId, form.grade);
        student_ids = (roster.items || []).map(s => s.student_id);
        if (!student_ids.length) {
          toast.error("No students in that grade");
          setPublishing(false);
          return;
        }
      }
      const r = await schoolSystemApi.publishPerformance({
        school_id: schoolId,
        academic_year: form.academic_year || null,
        term: form.term || null,
        student_ids,
        send_email: !!form.send_email,
        note: form.note || "",
      });
      toast.success(
        `Published ${r.published} · Notified ${r.notified} · Emailed ${r.emailed}`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  return (
    <div className="space-y-4" data-testid="ht-publish-perf-panel">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Publish approved performance to parents</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <F label="Academic year">
            <Input value={form.academic_year}
                   onChange={e => setForm({...form, academic_year: e.target.value})}
                   className="bg-white/5 border-white/10"
                   data-testid="pp-year"/>
          </F>
          <SelectField label="Term" value={form.term} onChange={v => setForm({...form, term: v})}
                        options={["Term 1","Term 2","Term 3"].map(t => ({ value: t, label: t }))}/>
          <SelectField label="Grade (blank = all)" value={form.grade || "__all__"}
                        onChange={v => setForm({...form, grade: v === "__all__" ? "" : v})}
                        options={[{value:"__all__",label:"All grades"}, ...GRADES.map(g=>({value:g,label:g}))]}/>
          <div className="md:col-span-2">
            <F label="Optional note to parents">
              <Textarea value={form.note} rows={2}
                        onChange={e => setForm({...form, note: e.target.value})}
                        className="bg-white/5 border-white/10"
                        data-testid="pp-note"/>
            </F>
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.send_email}
                     onChange={e => setForm({...form, send_email: e.target.checked})}
                     data-testid="pp-send-email"/>
              Also email parents the printable PDF
            </label>
          </div>
          <div className="md:col-span-3">
            <Button onClick={publish} disabled={publishing}
                    data-testid="pp-publish-btn"
                    className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
              {publishing ? "Publishing…" : "Publish now"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Past publications ({pubs.length})</CardTitle></CardHeader>
        <CardContent>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && pubs.length === 0 && (
            <p className="text-white/50 text-sm">No publications yet.</p>
          )}
          <div className="space-y-2">
            {pubs.slice(0, 30).map(p => (
              <div key={`${p.publication_id}-${p.student_id}`}
                   className="p-2 rounded-md bg-white/[0.02] border border-white/5 text-sm">
                <p className="font-semibold">
                  {p.student_id} · {p.term || "—"} · {p.academic_year || "—"}
                </p>
                <p className="text-white/50 text-xs">
                  {(p.per_subject || []).length} subjects · overall {p.overall?.average}% ·
                  published {(p.published_at || "").slice(0, 19).replace("T", " ")}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}



/* ------------------------------------------------------------------ *
 *  Branding — HT uploads school logo used on PDFs + email headers.   *
 * ------------------------------------------------------------------ */
function BrandingPanel({ schoolId }) {
  const [busy, setBusy] = useState(false);
  const [cacheBust, setCacheBust] = useState(Date.now());
  const logoUrl = `${schoolSystemApi.schoolLogoUrl(schoolId)}?t=${cacheBust}`;

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) {
      return toast.error("Please pick a PNG, JPEG or WEBP image.");
    }
    if (f.size > 400 * 1024) {
      return toast.error(`Image is ${Math.round(f.size / 1024)} KB — max 400 KB.`);
    }
    setBusy(true);
    try {
      const r = await schoolSystemApi.uploadSchoolLogo(schoolId, f);
      toast.success(`Logo uploaded (${Math.round(r.size / 1024)} KB)`);
      setCacheBust(Date.now());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="ht-branding">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">School logo</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-white/60 text-sm">
            This logo appears on the top of every generated PDF (Admission
            Register, Master Fees, Receipts, Performance, Report Card) and in
            the email header when the school messages parents. Recommended:
            square PNG or WEBP, transparent background, at least 256 × 256 px.
            Max 400 KB.
          </p>
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
              <img
                key={cacheBust}
                src={logoUrl}
                alt="School logo preview"
                className="max-w-full max-h-full"
                data-testid="ht-logo-preview"
              />
            </div>
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md ${busy ? "bg-white/5 cursor-wait" : "bg-emerald-500 hover:bg-emerald-600 cursor-pointer"} text-black font-bold`}>
              <input type="file" accept="image/png,image/jpeg,image/webp"
                     onChange={onPick} disabled={busy}
                     className="hidden" data-testid="ht-logo-input"/>
              {busy ? "Uploading…" : "Upload logo"}
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

